import { useContext, useEffect, useState } from "react";
import { LicenseHeaderBriefInfo } from "../../../../generated/data-contracts";
import {
  AvisDirection,
  INavigationLink,
  LicenseViewType,
} from "../../../../models";
import {
  IAxiosError,
  LicenseesUrls,
  LicenseesUtils,
  api,
} from "../../../../utils";
import { AvisForm } from "../../../shared/components";
import { AvisContext } from "../../../shared/context/AvisContext";
import { LicenseBriefInfo } from "./components/LicenseBriefInfo";
import { LicenseInvoices } from "./components/LicenseInvoices";
import { LicenseReports } from "./components/LicenseReports";
import { GroupedLicensesGrid } from "./groupedLicense/GroupedLicensesGrid";

interface ILicenseViewProps {
  viewType: LicenseViewType;
}
export function LicenseView(props: Readonly<ILicenseViewProps>) {
  const { viewType } = props;
  const { res } = useContext(AvisContext);
  const [error, setError] = useState<IAxiosError>();
  const [loadingBriefInfo, setLoadingBriefInfo] = useState<boolean>(true);
  const [licenseBriefInfo, setLicenseBriefInfo] = useState<
    LicenseHeaderBriefInfo | undefined
  >();
  useEffect(() => {
    const urlParams = LicenseesUtils.getUrlParams();

    if (!urlParams.licenseId) return;
    setLoadingBriefInfo(true);
    api
      .licenseesGetLicenseHeaderInfo(urlParams.licenseId)
      .then((res) => setLicenseBriefInfo(res.data))
      .catch(setError)
      .finally(() => setLoadingBriefInfo(false));
  }, []);

  const renderViewSectionComponent = () => {
    switch (viewType) {
      case LicenseViewType.Invoices:
        return <LicenseInvoices />;
      case LicenseViewType.Reports:
        return <LicenseReports licenseBriefInfo={licenseBriefInfo} />;
      case LicenseViewType.Grouped:
        return <GroupedLicensesGrid />;
      default:
        return;
    }
  };

  const navigationChoices = {
    toReports: {
      title: res("licensees_License_label_NavigateToReports"),
      icon: {
        className: "fa-chevron-right",
        location: AvisDirection.Right,
      },
      url: LicenseesUtils.getUrlWithParams(LicenseesUrls.licenseeLicenseView, {
        licenseId: licenseBriefInfo?.licenseHeaderId,
      }),
    },
    toInvoices: {
      title: res("licensees_License_label_NavigateToInvoices"),
      icon: {
        className: "fa-chevron-right",
        location: AvisDirection.Right,
      },
      url: LicenseesUtils.getUrlWithParams(
        LicenseesUrls.licenseeLicenseViewInvoices,
        {
          licenseId: licenseBriefInfo?.licenseHeaderId,
        },
      ),
    },
    toLicenseObjects: {
      title: res("licensees_License_label_NavigateToLicenseObjects"),
      icon: {
        className: "fa-chevron-right",
        location: AvisDirection.Right,
      },
      url: LicenseesUtils.getUrlWithParams(
        LicenseesUrls.licenseeLicenseViewChildLicenses,
        {
          licenseId: licenseBriefInfo?.licenseHeaderId,
        },
      ),
    },
  };

  const getForwardLink = (): INavigationLink | undefined => {
    switch (viewType) {
      case LicenseViewType.Invoices:
        return licenseBriefInfo?.isGroupingLicense
          ? navigationChoices.toLicenseObjects
          : navigationChoices.toReports;
      case LicenseViewType.Grouped:
      case LicenseViewType.Reports:
        return navigationChoices.toInvoices;
      default:
        return undefined;
    }
  };

  return (
    <AvisForm
      pageTitle={licenseBriefInfo?.licenseNumber}
      error={error}
      loading={loadingBriefInfo}
      backLink={{
        title: res("label_Back"),
        url: LicenseesUtils.getUrlWithParams(
          LicenseesUrls.licenseeLicenses,
          {},
          true,
        ),
      }}
      forwardLink={getForwardLink()}
    >
      <LicenseBriefInfo data={licenseBriefInfo} res={res} />
      {renderViewSectionComponent()}
    </AvisForm>
  );
}
